<template>
  <section class="ml-auto flex items-center justify-end">
    <ul
      v-if="totalItems > itemsPerPage"
      class="flex items-center justify-center sm:justify-end list-none m-0 !gap-0"
      style="width:min-content; text-wrap: nowrap;"
    >
      <!-- Previous page -->
      <li class="border border-gray rounded !rounded-r-none overflow-hidden">
        <AppButton
          class="bg-white !border-0 !rounded-none !p-0 hover:bg-gray-200"
          :class="{ 'pointer-events-none': isFirstPage }"
          @click="goToPage(page - 1)"
        >
          <span
            class="flex items-center justify-center w-22 h-9.5 text-black font-medium"
            :class="{ 'bg-gray-200 text-gray': isFirstPage }"
          >
            Previous
          </span>
        </AppButton>
      </li>

      <li
        v-for="(item, index) in paginatedPages"
        :key="index"
      >
        <span
          v-if="item === '...'"
          class="flex items-center justify-center w-10 h-10 border border-gray !border-l-0 bg-white"
        >
          ...
        </span>
        <AppButton
          v-else
          type="text"
          class="!p-0 border border-gray !rounded-none !border-l-0 w-10 h-10 hover:bg-gray-200"
          :class="item === page ? 'active text-white' : 'bg-white'"
          :value="page"
          :disabled="item === page"
          @click="goToPage(item)"
        >
          {{ item }}
        </AppButton>
      </li>

      <!-- Next page -->
      <li class="border border-gray rounded !border-l-0 !rounded-l-none overflow-hidden ">
        <AppButton
          class="bg-white !border-0 !rounded-none !p-0 hover:bg-gray-200"
          :class="{ 'pointer-events-none': isLastPage }"
          @click="goToPage(page + 1)"
        >
          <span
            :class="{ 'bg-gray-200 text-gray': isLastPage }"
            class="flex items-center justify-center w-22 h-9.5 text-black font-medium"
          >
            Next
          </span>
        </AppButton>
      </li>
    </ul>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import { AppButton } from '@/components/app';

const router = useRouter();

const props = defineProps({
  itemsPerPage: {
    type: Number,
    default: 10,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  totalItems: {
    type: Number,
    default: 0,
  },
  targetId: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['change']);

const page = ref(props.currentPage);
const totalPages = computed(() => (props.totalItems && props.itemsPerPage) ? Math.ceil(props.totalItems / props.itemsPerPage) : 2);
const isFirstPage = computed(() => page.value === 1);
const isLastPage = computed(() => page.value === totalPages.value);

const paginatedPages = computed(() => {
  const pages = [];
  const totalVisible = 7;

  if (totalPages.value <= totalVisible) {
    for (let i = 1; i <= totalPages.value; i++) {
      pages.push(i);
    }
  } else {
    // Always show the first page
    pages.push(1);

    // Logic to show pages around the current page
    let startPage = Math.max(2, page.value - 2);
    let endPage = Math.min(totalPages.value - 1, page.value + 2);

    if (page.value <= 4) {
      startPage = 2;
      endPage = 5;
    }

    if (page.value >= totalPages.value - 3) {
      startPage = totalPages.value - 4;
      endPage = totalPages.value - 1;
    }

    if (startPage > 2) {
      pages.push('...');
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages.value - 1) {
      pages.push('...');
    }

    // Always show the last page
    pages.push(totalPages.value);
  }

  return pages;
});

const goToPage = async (pageNumber) => {
  page.value = pageNumber;
  emit('change', pageNumber);

  await router.push({ hash: `#${props.targetId}` });
};

</script>

<style lang="css" scoped>
a {
  cursor: pointer;
}

.active {
  background: #056AB9;
}
</style>
